import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { demandAPI, offerAPI } from '../../services/api';
import { ProductListSkeleton } from '../../components/skeletons';
import { AttributeValues } from '../../components/AttributeFields';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 20 },
  searchRow: { display: 'flex', gap: 12, marginBottom: 16 },
  searchInput: { flex: 1, padding: '10px 16px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, background: '#fff', outline: 'none' },
  filterRow: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  filterBtn: (active) => ({
    padding: '6px 16px', borderRadius: 20, border: active ? 'none' : '1px solid var(--border)',
    background: active ? 'var(--green)' : '#fff', color: active ? '#fff' : 'var(--char)',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  }),
  card: { padding: 18, borderRadius: 12, background: '#fff', border: '1px solid var(--border)', marginBottom: 12 },
  tag: { display: 'inline-block', padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, marginRight: 6 },
  scoreBadge: (score) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 28, height: 28, borderRadius: '50%', fontSize: 11, fontWeight: 700, color: '#fff',
    background: score > 4 ? '#1B5C3A' : score > 3 ? '#F5A623' : '#DC2626',
  }),
  offerForm: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, padding: '12px 0', borderTop: '1px solid var(--border)' },
  priceInput: { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, width: 160 },
  emptyBox: { textAlign: 'center', padding: '48px 20px', background: '#fff', borderRadius: 12, border: '1px solid var(--border)' },
  activityBanner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 10,
    fontWeight: 600,
    color: '#0369A1',
    background: '#F0F9FF',
    border: '1px solid #B9E6FE',
    padding: '4px 10px',
    borderRadius: 8,
    marginBottom: 8,
    marginTop: 4,
  },
};

export default function BrowseDemands() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [demands, setDemands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [subFilter, setSubFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [offerPrice, setOfferPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [noFilters, setNoFilters] = useState(false);

  const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const dateStr = dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const timeStr = dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${dateStr}, ${timeStr}`;
  };

  useEffect(() => {
    demandAPI.list({ status: 'active' })
      .then(r => {
        setDemands(r.data.data || []);
        setNoFilters(!!r.data.noFilters);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  const categories = [...new Set(demands.map(d => d.categoryId?.name || d.category?.name || d.category).filter(Boolean))].filter(c => c !== 'Other');

  const subcategories = catFilter === 'all' ? [] : [...new Set(
    demands
      .filter(d => (d.categoryId?.name || d.category?.name || d.category || 'Other') === catFilter)
      .map(d => d.subCategoryId?.name)
      .filter(Boolean)
  )];

  const brands = catFilter === 'all' ? [] : [...new Set(
    demands
      .filter(d =>
        (d.categoryId?.name || d.category?.name || d.category || 'Other') === catFilter &&
        (subFilter === 'all' || d.subCategoryId?.name === subFilter)
      )
      .map(d => d.brandId?.name)
      .filter(Boolean)
  )];

  const compileSearchRegex = (s) => {
    if (!s) return null;
    const stripped = s.trim().replace(/\s+/g, '');
    if (!stripped) return null;
    const chars = [...stripped].map(char => char.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'));
    return new RegExp(chars.join('[\\s\\-_]*'), 'i');
  };

  const searchRegex = compileSearchRegex(search);

  const filtered = demands.filter(d => {
    const cat = d.categoryId?.name || d.category?.name || d.category || 'Other';
    const sub = d.subCategoryId?.name || '';
    const brand = d.brandId?.name || '';

    const matchCat = catFilter === 'all' || cat === catFilter;
    const matchSub = subFilter === 'all' || sub === subFilter;
    const matchBrand = brandFilter === 'all' || brand === brandFilter;

    const matchSearch = !searchRegex ||
      searchRegex.test(d.title || '') ||
      searchRegex.test(cat) ||
      searchRegex.test(sub) ||
      searchRegex.test(brand);

    return matchCat && matchSub && matchBrand && matchSearch;
  });

  const handleSendOffer = async (demandId) => {
    if (!offerPrice || Number(offerPrice) <= 0) return;
    setSubmitting(true);
    try {
      await offerAPI.submit({ demandId, price: Number(offerPrice) });
      setExpandedId(null);
      setOfferPrice('');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to send offer');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.title}>Browse Demands</div>
        <div style={S.subtitle}>Find buyer demands and send your best offers</div>
        <ProductListSkeleton count={6} columns={3} />
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.title}>Browse Demands</div>
      <div style={S.subtitle}>Find buyer demands and send your best offers</div>

      {/* Search */}
      <div style={S.searchRow}>
        <input
          style={S.searchInput}
          className="form-input"
          placeholder="Search demands by title or category..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Category Filters */}
      {categories.length > 0 && (
        <div style={S.filterRow}>
          {categories.map(c => (
            <button
              key={c}
              style={S.filterBtn(catFilter === c)}
              onClick={() => {
                setCatFilter(catFilter === c ? 'all' : c);
                setSubFilter('all');
                setBrandFilter('all');
              }}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Subcategory & Brand Selects */}
      {catFilter !== 'all' && (subcategories.length > 0 || brands.length > 0) && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
          {subcategories.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 750, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Sub-category</span>
              <select
                style={{
                  padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
                  fontSize: 13, background: '#fff', outline: 'none', minWidth: 160, fontWeight: 600, color: 'var(--char)'
                }}
                value={subFilter}
                onChange={e => {
                  setSubFilter(e.target.value);
                  setBrandFilter('all');
                }}
              >
                <option value="all">All Sub-categories</option>
                {subcategories.map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            </div>
          )}

          {brands.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 750, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Brand</span>
              <select
                style={{
                  padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)',
                  fontSize: 13, background: '#fff', outline: 'none', minWidth: 160, fontWeight: 600, color: 'var(--char)'
                }}
                value={brandFilter}
                onChange={e => setBrandFilter(e.target.value)}
              >
                <option value="all">All Brands</option>
                {brands.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Demand Cards */}
      {filtered.length === 0 ? (
        <div style={S.emptyBox}>
          {noFilters ? (
            <>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No Lead Filters Set</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Please add lead filters in Lead Filters section to see demands matching your business.</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--char)', marginBottom: 4 }}>No demands found</div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>Try adjusting your search or filters</div>
            </>
          )}
        </div>
      ) : (
        filtered.map(d => {
          const cat = d.categoryId?.name || d.category?.name || d.category || 'Other';
          const postedDemandsCount = d.buyerId?.postedDemandsCount ?? d.postedDemandsCount ?? 0;
          const locationText = d.nationwide
            ? 'Nationwide'
            : [d.location?.city, d.location?.state].filter(Boolean).join(', ');
          return (
            <div key={d._id} style={S.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ ...S.tag, background: 'var(--green-lt, #E8F5EE)', color: 'var(--green)' }}>{cat}</span>
                    {d.subCategoryId?.name && <span style={{ ...S.tag, background: '#FFF4D6', color: '#8A5A00' }}>{d.subCategoryId.name}</span>}
                    {d.brandId?.name && <span style={{ ...S.tag, background: 'var(--gray-lt, #F3F4F6)', color: 'var(--muted)' }}>{d.brandId.name}</span>}
                    {d.wantsLoan && <span style={{ ...S.tag, background: '#FFF4D6', color: '#8A5A00' }}>🏦 FINANCING</span>}
                    {d.isRepeat && <span style={{ ...S.tag, background: '#E0F2FE', color: '#0369A1' }}>🔄 REPEAT (FREE)</span>}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--char)', marginBottom: 4 }}>{d.title}</div>
                  <div style={S.activityBanner}>
                    <span>👤</span>
                    <span>Buyer: Total <strong>{postedDemandsCount} {postedDemandsCount === 1 ? 'demand' : 'demands'}</strong> posted</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--muted)', flexWrap: 'wrap' }}>
                    {locationText && <span>📍 {locationText}</span>}
                    {d.price != null && <span>💰 Buyer's price ₹{Number(d.price).toLocaleString('en-IN')}</span>}
                    <span>📦 Qty {d.qty || 1}</span>
                    {d.wantsLoan && d.downPayment != null && <span>💵 Down ₹{Number(d.downPayment).toLocaleString('en-IN')}</span>}
                    {d.createdAt && <span>📅 {formatDate(d.createdAt)}</span>}
                  </div>
                  {/* Exact specs the buyer chose — quote precisely, no follow-up needed */}
                  {Array.isArray(d.attributes) && d.attributes.length > 0 && (
                    <div style={{ marginTop: 8 }}><AttributeValues items={d.attributes} compact /></div>
                  )}
                </div>
                {user?.vendorType === 'financial' ? (
                  <button
                    className="btn btn-sm"
                    onClick={() => navigate(`/vendor/messages?demandId=${d._id}`)}
                    style={{
                      background: '#2563EB',
                      color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    }}>
                    💬 Chat with Buyer
                  </button>
                ) : (
                  <button
                    className="btn btn-sm"
                    onClick={() => { setExpandedId(expandedId === d._id ? null : d._id); setOfferPrice(''); }}
                    style={{
                      background: expandedId === d._id ? 'var(--muted)' : 'var(--green)',
                      color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}>
                    {expandedId === d._id ? 'Cancel' : 'Send Offer'}
                  </button>
                )}
              </div>

              {/* Inline Offer Form */}
              {expandedId === d._id && (
                <div style={S.offerForm}>
                  <span style={{ fontSize: 18, color: 'var(--char)' }}>{'\u20B9'}</span>
                  <input
                    style={S.priceInput}
                    className="form-input"
                    type="number"
                    placeholder="Your price"
                    value={offerPrice}
                    onChange={e => setOfferPrice(e.target.value)}
                    min="1"
                    onWheel={e => e.target.blur()}
                  />
                  <button
                    className="btn btn-pri btn-sm"
                    onClick={() => handleSendOffer(d._id)}
                    disabled={submitting || !offerPrice}
                    style={{
                      background: 'var(--green)', color: '#fff', border: 'none', padding: '8px 20px',
                      borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.6 : 1,
                    }}>
                    {submitting ? 'Sending...' : 'Send'}
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
